import { IconButton, Table, TableBody, TableCell, TableContainer, TableRow } from "@material-ui/core"
import DeleteIcon from "@material-ui/icons/Delete"
import EditIcon from "@material-ui/icons/Edit"
import { useEffect, useState } from "react"
import type { ReactElement } from "react"
import { connect } from "react-redux"

import { useTranslation } from "react-i18next"
import { usePageTracking } from "../../../../../hooks"
import { AnalyticsPageCategory, AnalyticsPageType } from "../../../../../models"
import type { PagingOptions } from "../../../../../models"
import { changeThemePage, deleteTheme, fetchAllThemes, history } from "../../../../../redux"
import type { AppState, AppThunkDispatch, Theme, ThemeActions } from "../../../../../redux"
import { CreateButton } from "../../../../atoms"
import { CoMaPagingBar, CrudDeleteDialog, CrudTableHeader, PageContainer } from "../../../../molecules"

export interface IThemeManagerOverviewProps {
  themes: Theme[]
  pagingOptions: PagingOptions
  fetchThemes: () => void
  deleteTheme: (id: string) => void
  changePage: (page: number) => void
}

function ThemeManagerOverviewPageView(props: IThemeManagerOverviewProps): ReactElement {
  const { themes, pagingOptions, fetchThemes, deleteTheme, changePage } = props

  const { t } = useTranslation("contentManager")

  const [dialogOpen, setDialogOpen] = useState<boolean>(false)
  const [idToRemove, setIdToRemove] = useState<string | undefined>(undefined)

  usePageTracking(AnalyticsPageCategory.ContentManager, AnalyticsPageType.Theme)

  useEffect(() => {
    fetchThemes()
  }, [fetchThemes])

  function onCreateButtonClicked() {
    history.push("/manage/themes/create")
  }

  function onEditButtonClicked(id: string) {
    history.push(`/manage/themes/${id}`)
  }
  function onPageChange(page: number): void {
    if (page < 0 || page >= pagingOptions.totalPages || page === pagingOptions.currentPage) {
      return
    }
    changePage(page)
  }

  function onDeleteButtonClicked(id: string) {
    setDialogOpen(true)
    setIdToRemove(id)
  }

  function onDialogClose(): void {
    setDialogOpen(false)
    setIdToRemove(undefined)
  }

  function onDeleteConfirm(): void {
    if (idToRemove) deleteTheme(idToRemove)
    onDialogClose()
  }

  return (
    <PageContainer id="ThemeManagerOverview" noPadding>
      <CreateButton onClick={() => onCreateButtonClicked()} />
      {pagingOptions.totalPages > 1 && (
        <CoMaPagingBar
          total={pagingOptions.totalPages}
          pageIndex={pagingOptions.currentPage}
          onPageChange={onPageChange}
        />
      )}
      <TableContainer>
        <Table>
          <CrudTableHeader
            headers={[
              { key: "name", label: t("themes.table.name") },
              {
                key: "parent",
                label: t("themes.table.belongsTo")
              }
            ]}
            onChange={fetchThemes}
          />
          <TableBody>
            {themes.map((theme: Theme, index: number) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: TODO fix in later PR
              <TableRow key={index}>
                <TableCell>{theme.name}</TableCell>
                <TableCell>{theme.parent}</TableCell>
                <TableCell padding="checkbox">
                  <IconButton onClick={() => onEditButtonClicked(theme.id as string)}>
                    <EditIcon />
                  </IconButton>
                </TableCell>
                <TableCell padding="checkbox">
                  <IconButton onClick={() => onDeleteButtonClicked(theme.id as string)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {pagingOptions.totalPages > 1 && (
        <CoMaPagingBar
          total={pagingOptions.totalPages}
          pageIndex={pagingOptions.currentPage}
          onPageChange={onPageChange}
        />
      )}
      <CrudDeleteDialog open={dialogOpen} onClose={onDialogClose} possibleCascade onConfirm={onDeleteConfirm} />
    </PageContainer>
  )
}

function mapStateToProps(state: AppState) {
  return {
    themes: state.theme.themes,
    pagingOptions: state.theme.pagingOptions
  }
}

function mapDispatchToProps(dispatch: AppThunkDispatch<ThemeActions>) {
  return {
    fetchThemes: () => dispatch(fetchAllThemes()),
    deleteTheme: (id: string) => dispatch(deleteTheme(id)),
    changePage: (page: number) => dispatch(changeThemePage(page))
  }
}

export const ThemeManagerOverviewPage = connect(mapStateToProps, mapDispatchToProps)(ThemeManagerOverviewPageView)
